if (!customElements.get("swiper-slider")) {
  class SwiperSlider extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.initialized = false;
      this.resizeTimeout = null;
      this.boundHandlers = {
        initializeSwiper: this.initializeSwiper.bind(this),
        handleResize: this.handleResize.bind(this),
        updateSlides: this.updateSlides.bind(this),
        refresh: this.refresh.bind(this),
        goToSlide: this.goToSlide.bind(this),
        nextSlide: this.nextSlide.bind(this),
        prevSlide: this.prevSlide.bind(this)
      };
    }

    connectedCallback() {
      this.slideContainer = this.querySelector(".swiper");
      this.slides = this.querySelectorAll(".swiper-slide");
      this.slidesCount = this.slides?.length || 0;
      this.sliderOptions = this.applyDefaultOptions(this.getSliderOptions());
      this.nextSlideButton = this.querySelector(".custom-button-next");
      this.prevSlideButton = this.querySelector(".custom-button-prev");
      this.fraction = this.querySelector("[data-swiper-fraction]") || false;
      this.isNonCritical = this.hasAttribute('data-non-critical');

      if (this.dataset.swiperThumbs) {
        this.thumbnails = this.querySelector('[data-swiper-thumbs]');
      }

      if (this.dataset.customEvent) {
        this.customEventType = this.dataset.customEvent;
        this.attachCustomListeners(this.customEventType);
      }

      if (this.slideContainer && this.slidesCount > 0) {
        if (document.readyState === 'complete') {
          this.scheduleInit();
        } else {
          document.addEventListener(
            "DOMContentLoaded",
            () => this.scheduleInit(),
            { once: true }
          );
        }

        document.addEventListener(
          "shopify:section:load",
          this.boundHandlers.initializeSwiper
        );

        window.addEventListener("resize", this.boundHandlers.handleResize);
        this.addEventListener("update-slides", this.boundHandlers.updateSlides);
        this.addEventListener("refresh", this.boundHandlers.refresh);
        this.addEventListener("go-to-slide", this.boundHandlers.goToSlide);

        if (this.nextSlideButton) {
          this.nextSlideButton.addEventListener(
            "click",
            this.boundHandlers.nextSlide
          );
        }

        if (this.prevSlideButton) {
          this.prevSlideButton.addEventListener(
            "click",
            this.boundHandlers.prevSlide
          );
        }
      } else {
        console.warn("SwiperSlider - Slider or Slides Not Found");
      }
    }

    scheduleInit() {
      // Use requestIdleCallback for non-critical swipers to avoid blocking main thread
      if (this.isNonCritical && 'requestIdleCallback' in window) {
        this.idleCallbackId = requestIdleCallback(
          () => this.initializeSwiper(),
          { timeout: 2000 } // Fallback timeout of 2s
        );
      } else {
        this.initializeSwiper();
      }
    }

    disconnectedCallback() {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = null;
      }

      // Cancel pending idle callback if element removed before init
      if (this.idleCallbackId && 'cancelIdleCallback' in window) {
        cancelIdleCallback(this.idleCallbackId);
        this.idleCallbackId = null;
      }

      document.removeEventListener(
        "shopify:section:load",
        this.boundHandlers.initializeSwiper
      );
      window.removeEventListener("resize", this.boundHandlers.handleResize);
      this.removeEventListener("update-slides", this.boundHandlers.updateSlides);
      this.removeEventListener("refresh", this.boundHandlers.refresh);
      this.removeEventListener("go-to-slide", this.boundHandlers.goToSlide);

      if (this.nextSlideButton) {
        this.nextSlideButton.removeEventListener(
          "click",
          this.boundHandlers.nextSlide
        );
      }

      if (this.prevSlideButton) {
        this.prevSlideButton.removeEventListener(
          "click",
          this.boundHandlers.prevSlide
        );
      }

      if (this.customEventType && this.boundHandlers.customEvent) {
        document.removeEventListener(
          this.customEventType,
          this.boundHandlers.customEvent
        );
      }

      if (this.slider) {
        this.slider.destroy(true, true);
        this.slider = null;
        this.initialized = false;
      }
    }
    nextSlide() {
      this.slider.slideNext();
    }
    prevSlide() {
      this.slider.slidePrev();
    }

    updateSlides(evt) {
      this.slider.appendSlide(evt.detail.slides);
      this.goToSlide(evt);
      this.slider.update();
    }

    refresh(evt) {
      this.slider.update();
    }

    goToSlide(evt) {
      let index = evt.detail.index;
      if (!index && evt.detail.position) {
        const elm = this.querySelector(
          "[data-position='" + evt.detail.position + "']"
        );
        if (elm && elm.parentNode) {
          const children = elm.parentNode.children;
          for (let i = 0; i < children.length; i++) {
            if (children[i] === elm) {
              index = i;
              break;
            }
          }
        }
      }
      if (index != 0 && !index) return;
      this.slider.slideTo(index);
    }

    applyDefaultOptions(options) {
      if (options.loop === undefined) {
        options.loop = true;
      }
      options.speed = 400;
      return options;
    }

    getSliderOptions() {
      try {
        let options = JSON.parse(this.dataset.swiperOptions);
        let bullets = this.dataset.swiperBullets;
        if (bullets) {
          bullets = bullets.split(",");
          options.pagination.renderBullet = function (index, className) {
            return (
              '<p class="swiper-pagination-bullet--text ' +
              className +
              '">' +
              bullets[index] +
              "</p>"
            );
          };
        }
        return options;
      } catch (err) {
        console.log(err);
        return {};
      }
    }

    initializeSwiper() {
      if (window.Swiper && !this.initialized) {
        this.initialized = true;
        if(this.thumbnails) {
          this.setThumbnails()
        }
        if(this.fraction) {
          const fraction = this.fraction;
          const slidesCount = this.slidesCount;
          this.sliderOptions.on = {
            slideChange: function () {
              let position = this.activeIndex;
              if(position == 0) {
                position = slidesCount;
              } else if(position > slidesCount) {
                position = position - slidesCount;
              }
              fraction.textContent = `${position} / ${slidesCount}`;
            }
          }
        }
        let activeSlideIndex = -1;
        for (let i = 0; i < this.slides.length; i++) {
          if (this.slides[i].classList.contains('swiper-slide-active')) {
            activeSlideIndex = i;
            break;
          }
        }
        if (activeSlideIndex > 0) {
          this.sliderOptions.initialSlide = activeSlideIndex;
        }
        this.slider = new Swiper(this.slideContainer, this.sliderOptions);
        this.initializeSliderEventListeners();
      } else if (!window.Swiper) {
        console.warn("SwiperSlider - Swiper.js not found");
      }
    }

    handleResize(e) {
      if (this.slider) {
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
        }
        this.resizeTimeout = setTimeout(() => {
          this.slider.updateSize();
          this.resizeTimeout = null;
        }, 300);
      }
    }

    setThumbnails() {
      const thumbsSlideContainer = this.thumbnails;
      const thumbOptions = JSON.parse(this.thumbnails.dataset.swiperOptions);
      const thumbnailsSlider = new Swiper(thumbsSlideContainer, thumbOptions);
      this.sliderOptions.thumbs = {swiper: thumbnailsSlider};
    }

    attachCustomListeners(customEvent) {
      switch (customEvent) {
        case "swiper_event":
          this.boundHandlers.customEvent = this.boundHandlers.updateSlides;
          document.addEventListener("swiper_event", this.boundHandlers.customEvent);
          break;
      }
    }


    initializeSliderEventListeners() {
      if (this.slider) {
        this.slider.on('slideChange', () => {
          const activeSlide = this.slider.slides[this.slider.activeIndex];
          const event = new CustomEvent('slide-changed', {
            bubbles: true,
            detail: { 
              activeSlide: activeSlide || null,
              sliderId: this.id || null
            }
          });
          this.dispatchEvent(event);
        });
      }
    }
  }
  customElements.define("swiper-slider", SwiperSlider);






  class SwiperTabSlider extends SwiperSlider {
    constructor() {
      super();
      this.boundTabHandlers = new Map();
    }

    connectedCallback() {
      super.connectedCallback();
      this.tabs = this.querySelectorAll(".glide__nav-item");
      if (this.tabs.length > 0) {
        this.tabs.forEach((tab) => {
          const handler = this.goToSlide.bind(this, tab);
          this.boundTabHandlers.set(tab, handler);
          tab.addEventListener("click", handler);
        });
      }
    }

    disconnectedCallback() {
      if (this.tabs && this.boundTabHandlers) {
        this.tabs.forEach((tab) => {
          const handler = this.boundTabHandlers.get(tab);
          if (handler) {
            tab.removeEventListener("click", handler);
          }
        });
        this.boundTabHandlers.clear();
      }
      super.disconnectedCallback();
    }

    goToSlide(tab) {
      try {
        const tabIndex = tab.dataset.newIndex;
        if (tabIndex) {
          this.slider.slideTo(tabIndex);
        } else {
          console.warn("newIndex not provided for Tab Slider Tab");
        }
      } catch (err) {
        console.warn(err);
      }
    }
  }

  customElements.define("swiper-tab-slider", SwiperTabSlider);
}
